import { useEffect, useRef } from "react"
import { View, StyleSheet, Platform } from "react-native"
import { TouchableOpacity } from "react-native"

import { colors, spacing, typography } from "@/theme"

import { Icon } from "./Icon"
import { Modal } from "./Modal.web"
import { Text } from "./Text"

const WIDGET_SCRIPT_URL = "https://booking.holdeineplakette.de/widget.js"

interface RescheduleModalProps {
  visible: boolean
  onClose: () => void
  hubName?: string
}

export const RescheduleModal: React.FC<RescheduleModalProps> = ({ visible, onClose, hubName }) => {
  const widgetContainerRef = useRef<View>(null)

  // Mount/unmount HDP booking widget when modal visibility changes
  useEffect(() => {
    if (!visible || Platform.OS !== "web" || typeof document === "undefined") return

    const containerNode = widgetContainerRef.current as unknown as HTMLDivElement
    if (!containerNode) return

    // Create mount point for the widget
    const mountDiv = document.createElement("div")
    mountDiv.id = "hdp-booking"
    if (hubName) {
      mountDiv.setAttribute("data-hub", hubName)
    }
    mountDiv.setAttribute("data-order-source", "b2c-website-carassist-tracking-rebook")
    mountDiv.style.width = "100%"
    mountDiv.style.height = "100%"
    mountDiv.style.fontFamily =
      "'Overused Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    containerNode.appendChild(mountDiv)

    // Load the widget script — IIFE auto-mounts into #hdp-booking
    const script = document.createElement("script")
    script.src = WIDGET_SCRIPT_URL
    script.async = true
    document.head.appendChild(script)

    return () => {
      // Cleanup: remove mount div and script
      if (mountDiv.parentNode) {
        mountDiv.parentNode.removeChild(mountDiv)
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [visible, hubName])

  if (Platform.OS !== "web") {
    return null
  }

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      style={styles.modalContent}
      animationType="slide"
      backdropOpacity={0.6}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>Termin verpasst? Kein Problem!</Text>
            <Text style={styles.subtitle}>Einfach neuen Termin vereinbaren</Text>
          </View>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Icon icon="x" size={24} color={colors.textDim} />
          </TouchableOpacity>
        </View>

        {/* HDP Booking Widget Container */}
        <View ref={widgetContainerRef} style={styles.widgetContainer} />
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  closeButton: {
    backgroundColor: colors.modalScrim,
    borderRadius: 20,
    padding: spacing.xs,
  },
  container: {
    backgroundColor: colors.surface,
    flex: 1,
  },
  header: {
    alignItems: "center",
    borderBottomColor: colors.modalBorder,
    borderBottomWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  modalContent: {
    height: "85vh" as any,
    maxHeight: "85vh" as any,
    maxWidth: 900,
    overflow: "hidden",
    padding: 0,
    width: "95%",
  },
  subtitle: {
    color: colors.modalHelperText,
    fontFamily: typography.primary.normal,
    fontSize: 14,
    marginTop: 2,
  },
  title: {
    color: colors.text,
    fontFamily: typography.primary.semiBold,
    fontSize: 20,
  },
  widgetContainer: {
    backgroundColor: colors.surface,
    flex: 1,
    overflow: "scroll" as any,
    padding: spacing.md,
  },
})
